import type { paths } from '@/generated/contracts/api-v1';
import { neverCachedReads } from '@/generated/contracts/api-v1.cache';

/**
 * Which region of the backend a request goes to. The backend runs in several Fly regions; a region answers reads from
 * its own cache, and everything else is answered in Frankfurt, where the database is. Fly's proxy takes a request that
 * carries `fly-force-region: fra` straight to Frankfurt, with no extra hop, so the client sends it on every write, on
 * the change streams and on the routes that are never cached, with `X-Lureo-Asked-Home` saying which of those it is.
 *
 * Every other read goes to the nearest region. A region's copy of the cache can lag Frankfurt's by a moment, and the
 * user should always read back what they just saved. So a write's answer names the version of the cache it needs
 * (`X-Lureo-Cache-Seq`), and every read that may be answered nearby carries the highest this tab has seen
 * (`X-Lureo-Read-After`): a region whose copy is behind it sends the read on to Frankfurt itself. A change event from
 * either stream names a version too (`cacheSeq`): the stream drops the change's cache entries in Frankfurt before it
 * sends the event, so the reads the change makes the page make again carry it in the same way. A write still on its
 * way holds no read in Frankfurt: a read sent meanwhile was never ordered after it, even there. Only where there is no
 * version to wait for, after a write answered without one (its drop failed, or no answer came), a change event that
 * names none, or a change stream that reconnected (the events it missed are lost), do the reads that may be answered
 * nearby go to Frankfurt instead for a while, asked home `after-write`.
 */

export const FORCE_REGION_HEADER = 'fly-force-region';

/** Frankfurt: where the database is, and every write lands. */
export const HOME_REGION = 'fra';

/** On a write's answer: the version of the cache its reads need, the highest its drops reached; 0 when it dropped nothing. */
export const CACHE_SEQ_HEADER = 'X-Lureo-Cache-Seq';

/** On a read that may be answered nearby: the highest version this tab has seen, which a region's copy must have reached. */
export const READ_AFTER_HEADER = 'X-Lureo-Read-After';

/** On a request sent to Frankfurt, why it was (AskedHome): Fly's proxy takes `fly-force-region` off, so the backend learns it from this. */
export const ASKED_HOME_HEADER = 'X-Lureo-Asked-Home';

/**
 * Why a request is sent to Frankfurt: it is a write, a read no region caches (the change streams among them), or a read
 * a region may cache sent within READ_YOUR_WRITES_MS of a write or change that named no version.
 */
export type AskedHome = 'write' | 'never-cached' | 'after-write';

/**
 * How long after a write answered without its version, a change event that names none, or a change stream reconnecting,
 * this tab's reads go to Frankfurt too: there is no version for a region's copy of the cache to have reached.
 */
export const READ_YOUR_WRITES_MS = 10_000;

/**
 * What a change stream's event says of the cache: the version the stream's drop of the change's cache entries reached
 * (0 when it dropped nothing), absent when the drop didn't reach the cache or the backend names none. The streams'
 * event types carry it with this, whether or not the generated contract names it yet.
 */
export interface ChangeCacheSeq {
  cacheSeq?: number | null;
}

/** The change streams: pushed from Frankfurt, where the database's change events are heard. */
export const CHANGE_STREAMS = [
  '/api/v1/scenes/{sceneUniqueId}/changes',
  '/api/v1/users/me/changes',
] as const satisfies readonly (keyof paths)[];

/**
 * The reads the backend never caches, every one the contract marks `x-lureo-cache: none` (generated from it): its
 * version, the session bootstrap and the signed-in user, remote config, job and conversion status, MCP authorizations
 * and connections, external content, the storage reads, resolving a PIN or share link, and the change streams.
 */
export const NEVER_CACHED = neverCachedReads satisfies readonly (keyof paths)[];

/**
 * Writes that change no cached read the tab shows: the client's sighting (reportClientSighting) and the session
 * operations (createSession, refreshSession, deleteCurrentSession, createSignUp, createOAuthAuthorization,
 * createPasswordReset). They go to Frankfurt like every write, and a version their answer names is taken as any
 * write's is (a sign-up creates the new account's org, which the cache drops), but one answered without a version
 * sends no read to Frankfurt: without this, the sighting every launch sends could send the first screen's reads there.
 */
export const WRITES_NO_READ_SHOWS = [
  '/api/v1/client-sightings',
  '/api/v1/sessions',
  '/api/v1/sessions/refresh',
  '/api/v1/sessions/current',
  '/api/v1/sign-ups',
  '/api/v1/oauth-authorizations',
  '/api/v1/password-resets',
] as const satisfies readonly (keyof paths)[];

/**
 * A path template's pattern: each `{param}` matches one path segment, but a stored object's `{path}` at the end matches
 * the rest of the path, slashes and all (getStorageObject's path has a slash between segments).
 */
const pattern = (template: string) =>
  new RegExp(`^${template.replace(/[.*+?^$()|[\]\\]/g, '\\$&').replace(/\{path\}$/, '.+').replace(/\{[^}]+\}/g, '[^/]+')}$`);

const matcher = (templates: readonly string[]) => {
  const patterns = templates.map(pattern);
  return (path: string | null) => !!path && patterns.some(each => each.test(path));
};

const isChangeStream = matcher(CHANGE_STREAMS);
const isNeverCached = matcher(NEVER_CACHED);
const changesNoRead = matcher(WRITES_NO_READ_SHOWS);

/** The request's path from `/api/v1/` on, or null when it is not a v1 request. */
export function apiPath(url: string): string | null {
  let pathname: string;
  try {
    pathname = new URL(url, 'http://relative.invalid').pathname;
  } catch {
    return null;
  }
  const at = pathname.indexOf('/api/v1/');
  return at < 0 ? null : pathname.slice(at);
}

const isRead = (method: string) => method === 'GET' || method === 'HEAD';

/**
 * Why a request goes to Frankfurt whenever it is sent: a write, a change stream or route that is never cached, or a
 * HEAD (a region's cache answers only a GET, and sends anything else home itself); null for a read the nearest region
 * may answer.
 */
export function askedHome(method: string, url: string): Exclude<AskedHome, 'after-write'> | null {
  const upper = method.toUpperCase();
  if (!isRead(upper)) return 'write';
  const path = apiPath(url);
  return upper === 'HEAD' || isChangeStream(path) || isNeverCached(path) ? 'never-cached' : null;
}

/** Whether a request is one of the change streams. */
export const changeStream = (url: string) => isChangeStream(apiPath(url));

/**
 * The version of the cache an answer names (`X-Lureo-Cache-Seq`), or null when it names none a read could carry: no
 * answer, no header (an older backend, or one the browser may not read), or a value that isn't a whole number.
 */
export function cacheSeq(headers: Headers | null | undefined): number | null {
  const value = headers?.get(CACHE_SEQ_HEADER)?.trim();
  if (!value || !/^\d+$/.test(value)) return null;
  const seq = Number(value);
  return Number.isSafeInteger(seq) ? seq : null;
}

/** Whether a change event's `cacheSeq` is a version a read could carry: a whole number, 0 or more. */
const isVersion = (value: unknown): value is number => typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;

export interface RegionRouting {
  /**
   * Sets `fly-force-region` and `X-Lureo-Asked-Home` on these headers when the request must be answered in Frankfurt:
   * askedHome's reason, or `after-write` for a read sent within READ_YOUR_WRITES_MS of a write or change that named no
   * version. On any other read, sets `X-Lureo-Read-After` once this tab has seen a version. Returns them.
   */
  route(method: string, url: string, headers: Headers): Headers;
  /**
   * Notes that a request routed here has answered, with its answer's headers, or failed (none). A write's version
   * raises the one reads carry; a write answered without one, or not answered, sends reads to Frankfurt for
   * READ_YOUR_WRITES_MS, unless it changes no cached read (WRITES_NO_READ_SHOWS). A read's answer changes nothing.
   */
  answered(method: string, url: string, headers?: Headers | null): void;
  /**
   * Notes a change event from a change stream (another tab, Claude), with the version of the cache it names. That
   * version raises the one reads carry, as a write's does, so the reads the change sets off are answered nearby only
   * from a copy of the cache that has it. One that names none, as the changes a reconnected stream missed name none,
   * sends reads to Frankfurt for READ_YOUR_WRITES_MS, since a region's copy may still hold the answer from before it.
   */
  changedElsewhere(cacheSeq?: number | null): void;
  /**
   * Raises the version reads carry to this one when it is a version, and does nothing else: never opens the window,
   * and tells no screen of a change. For a version this tab already has the change for: one its own change, echoed on
   * a change stream, names, or one another of the user's tabs has seen (shareCacheVersion).
   */
  raiseReadAfter(cacheSeq?: number | null): void;
  /**
   * Sends the reads that may be answered nearby to Frankfurt until at least `until` (`now`'s clock), as another of the
   * user's tabs does after a write or change with no version (shareCacheVersion). An end later than this tab's, and
   * still to come, opens or moves on its window and is told as one this tab opened is; any other changes nothing.
   */
  readHomeAtLeastUntil(until: number): void;
  /** The highest version of the cache a write's answer or a change event has named, which reads that may be answered nearby carry; 0 before any. */
  readAfter(): number;
  /**
   * Calls `listener` with that version each time it rises, as the write's answer or the change event that raised it is
   * noted, and never for one that leaves it as it was; returns what stops it. It is for what reads on the tab's behalf
   * without writing through it (the renderer), so that its reads carry the version too.
   */
  onReadAfterRise(listener: (readAfter: number) => void): () => void;
  /** When the window that sends reads to Frankfurt ends, in milliseconds since the epoch (`now`'s clock); in the past while none is open. */
  readHomeUntil(): number;
  /**
   * Calls `listener` with the window's end each time a write or change with no version opens it (or moves its end on),
   * as onReadAfterRise does for the version; returns what stops it. It is for the renderer, whose reads must honour it.
   */
  onReadHome(listener: (until: number) => void): () => void;
}

export function regionRouting(now: () => number = () => Date.now()): RegionRouting {
  // Until when reads go to Frankfurt: READ_YOUR_WRITES_MS after a write was last answered without its version, or a change
  // named none, here or in another of the user's tabs
  let homeUntil = Number.NEGATIVE_INFINITY;
  // The highest version a write's answer or a change event named: it only ever grows, so a read never asks for less than one before it
  let seen = 0;
  const rises = new Set<(readAfter: number) => void>();
  const windows = new Set<(until: number) => void>();
  // Told as it happens, before the answer reaches its caller; a listener that fails never fails the request it heard of
  const tell = <T>(listeners: Set<(value: T) => void>, value: T) => {
    for (const listener of [...listeners]) {
      try {
        listener(value);
      } catch (error) {
        console.error('[routing] A routing listener failed:', error);
      }
    }
  };
  const see = (seq: number) => {
    if (seq <= seen) return;
    seen = seq;
    tell(rises, seq);
  };
  // An end later than the one set, and still to come, opens the window or moves it on
  const holdHome = (until: number) => {
    if (until <= homeUntil || until <= now()) return;
    homeUntil = until;
    tell(windows, until);
  };
  const goHome = () => holdHome(now() + READ_YOUR_WRITES_MS);
  // Each call its own entry, so one that subscribes the same function twice stops only its own
  const listen = <T>(listeners: Set<(value: T) => void>, listener: (value: T) => void) => {
    const each = (value: T) => listener(value);
    listeners.add(each);
    return () => void listeners.delete(each);
  };
  return {
    route(method, url, headers) {
      const asked = askedHome(method, url) ?? (now() < homeUntil ? 'after-write' : null);
      if (asked) {
        headers.set(FORCE_REGION_HEADER, HOME_REGION);
        headers.set(ASKED_HOME_HEADER, asked);
      } else if (seen > 0) {
        headers.set(READ_AFTER_HEADER, String(seen));
      }
      return headers;
    },
    answered(method, url, headers) {
      // A read's answer names no version
      if (isRead(method.toUpperCase())) return;
      // 0 is a version (the write dropped nothing); none is a drop that failed, or a write that got no answer, which the
      // client's sighting and the session operations may be without sending reads home: they change nothing a read shows
      const seq = cacheSeq(headers);
      if (seq !== null) see(seq);
      else if (!changesNoRead(apiPath(url))) goHome();
    },
    changedElsewhere(seq) {
      if (isVersion(seq)) see(seq);
      else goHome();
    },
    raiseReadAfter(seq) {
      if (isVersion(seq)) see(seq);
    },
    readHomeAtLeastUntil(until) {
      if (Number.isFinite(until)) holdHome(until);
    },
    readAfter: () => seen,
    onReadAfterRise: listener => listen(rises, listener),
    readHomeUntil: () => homeUntil,
    onReadHome: listener => listen(windows, listener),
  };
}

/**
 * How long a version kept for the user's tabs (shareCacheVersion) is taken from where it is kept: far longer than any
 * region's copy of the cache takes to reach a version, so one kept longer ago has been reached everywhere.
 */
export const SHARED_VERSION_MS = 10 * 60_000;

/** What shareCacheVersion keeps for the user's tabs: the highest version, when it was kept, and the window's end. */
interface SharedVersion {
  readAfter: number;
  /** When `readAfter` was kept, in milliseconds since the epoch by the clock the user's tabs share. */
  at: number;
  readHomeUntil: number;
}

const NOTHING_SHARED: SharedVersion = { readAfter: 0, at: 0, readHomeUntil: 0 };

/** What is kept, or null for nothing a tab could take. */
function parseShared(text: string | null | undefined): SharedVersion | null {
  try {
    const { readAfter, at, readHomeUntil } = (JSON.parse(text ?? 'null') ?? {}) as Partial<Record<keyof SharedVersion, unknown>>;
    const time = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);
    return isVersion(readAfter) && time(at) && time(readHomeUntil) ? { readAfter, at, readHomeUntil } : null;
  } catch {
    return null;
  }
}

/**
 * Where the user's tabs keep it, the page's window: its localStorage, any use of which may throw (a private window,
 * blocked site data), and the storage event.
 */
export interface VersionHost {
  readonly localStorage: Pick<Storage, 'getItem' | 'setItem'>;
  addEventListener(type: 'storage', listener: (event: StorageEvent) => void): void;
  removeEventListener(type: 'storage', listener: (event: StorageEvent) => void): void;
}

/**
 * Shares the highest version of the cache, and the window that sends reads to Frankfurt, across the user's tabs of the
 * app, kept in localStorage under `key`, so a change saved in one tab is never read from a region's older copy in
 * another, nor in this one once it reloads:
 *  - as it starts, before it sends anything, the tab takes what is kept, so a new or reloaded tab starts with the
 *    highest version any tab has had, and an open window, and nothing waits;
 *  - each rise of this tab's version, and each window it opens, is kept when it is higher than what is kept, and the
 *    other tabs take it as it is kept (the storage event): a version only raises theirs, opening no window, and a
 *    window still to come opens theirs. What a tab takes is kept already, so it is never kept again.
 * A version kept too high costs time, never a wrong answer: a region whose copy is below a read's version sends it to
 * Frankfurt. Kept for good, though, one from before the cache's counter started again (its store lost) would send every
 * read from this browser there until the counter passed it, so a version kept more than SHARED_VERSION_MS ago is not
 * taken. Two tabs keeping at one moment can leave the lower of the two kept: each still reads with its own, and the
 * next rise is kept. Where localStorage can't be used, each tab keeps its own, as a tab alone does. Returns what stops it.
 */
export function shareCacheVersion(routing: RegionRouting, key: string, host: VersionHost, now: () => number = () => Date.now()): () => void {
  const kept = () => {
    try {
      return parseShared(host.localStorage.getItem(key));
    } catch {
      return null;
    }
  };
  const keep = (shared: SharedVersion | null, change: Partial<SharedVersion>) => {
    try {
      host.localStorage.setItem(key, JSON.stringify({ ...(shared ?? NOTHING_SHARED), ...change }));
    } catch {
      // Not kept: this tab reads with its own
    }
  };
  const fresh = (shared: SharedVersion) => now() - shared.at <= SHARED_VERSION_MS;
  const take = (shared: SharedVersion | null) => {
    if (!shared) return;
    if (fresh(shared)) routing.raiseReadAfter(shared.readAfter);
    routing.readHomeAtLeastUntil(shared.readHomeUntil);
  };

  take(kept());
  const stopRises = routing.onReadAfterRise(readAfter => {
    const shared = kept();
    if (!shared || !fresh(shared) || shared.readAfter < readAfter) keep(shared, { readAfter, at: now() });
  });
  const stopWindows = routing.onReadHome(until => {
    const shared = kept();
    if (!shared || shared.readHomeUntil < until) keep(shared, { readHomeUntil: until });
  });
  const onStorage = (event: StorageEvent) => {
    if (event.key === key) take(parseShared(event.newValue));
  };
  try {
    host.addEventListener('storage', onStorage);
  } catch {
    // A page that can't hear the others keeps what it took as it started
  }
  return () => {
    stopRises();
    stopWindows();
    try {
      host.removeEventListener('storage', onStorage);
    } catch {
      // Never heard
    }
  };
}
